/**
 * Performance monitoring utilities for SwipeUI complex state updates
 * Tracks timing and performance metrics during intensive operations
 */

import React from 'react';

interface PerformanceMetric {
  name: string;
  startTime: number;
  duration?: number;
  metadata?: Record<string, any>;
}

class PerformanceMonitor {
  private metrics: Map<string, PerformanceMetric> = new Map();
  private readonly isProduction = process.env.NODE_ENV === 'production';
  
  /**
   * Start timing a performance metric
   */
  start(name: string, metadata?: Record<string, any>): void {
    if (this.isProduction) return;
    
    this.metrics.set(name, {
      name,
      startTime: performance.now(),
      metadata
    });
  }
  
  /**
   * End timing and log the result
   */
  end(name: string): number | null {
    if (this.isProduction) return null;
    
    const metric = this.metrics.get(name);
    if (!metric) {
      console.warn(`⚠️ [PerformanceMonitor] No start time found for: ${name}`);
      return null;
    }
    
    const duration = performance.now() - metric.startTime;
    metric.duration = duration;
    
    // Log slow operations
    if (duration > 16) { // Slower than 60fps
      console.log(`🐌 [PerformanceMonitor] Slow operation: ${name} (${duration.toFixed(2)}ms)`, metric.metadata);
    } else {
      console.log(`⚡ [PerformanceMonitor] ${name}: ${duration.toFixed(2)}ms`, metric.metadata);
    }
    
    this.metrics.delete(name);
    return duration;
  }
  
  /**
   * Time a function execution
   */
  measure<T>(name: string, fn: () => T, metadata?: Record<string, any>): T {
    if (this.isProduction) return fn();
    
    this.start(name, metadata);
    try {
      const result = fn();
      this.end(name);
      return result;
    } catch (error) {
      this.end(name);
      throw error;
    }
  }
  
  /**
   * Time an async function execution
   */
  async measureAsync<T>(name: string, fn: () => Promise<T>, metadata?: Record<string, any>): Promise<T> {
    if (this.isProduction) return fn();
    
    this.start(name, metadata);
    try {
      const result = await fn();
      this.end(name);
      return result;
    } catch (error) {
      this.end(name);
      throw error;
    }
  }
  
  /**
   * Get current performance metrics summary
   */
  getMetrics(): PerformanceMetric[] {
    return Array.from(this.metrics.values());
  }
  
  /**
   * Clear all metrics
   */
  clear(): void {
    this.metrics.clear();
  }
}

export const performanceMonitor = new PerformanceMonitor();

/**
 * React hook for measuring component render performance
 */
export function usePerformanceMonitor(componentName: string) {
  const startRender = () => performanceMonitor.start(`${componentName}-render`);
  const endRender = () => performanceMonitor.end(`${componentName}-render`);
  
  return { startRender, endRender };
}

/**
 * Higher-order component to automatically measure render performance
 */
export function withPerformanceMonitoring<P extends object>(
  Component: React.ComponentType<P>,
  componentName?: string
): React.ComponentType<P> {
  const name = componentName || Component.displayName || Component.name;
  
  return function PerformanceMonitoredComponent(props: P) {
    performanceMonitor.start(`${name}-render`);
    
    React.useEffect(() => {
      performanceMonitor.end(`${name}-render`);
    });
    
    return React.createElement(Component, props);
  };
}